import { useEffect, useMemo, useState } from "react";

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Client-side pagination for list UIs that load full arrays from the API.
 */
export function useClientPagination(items, options = {}) {
  const initialPageSize = options.initialPageSize ?? 10;
  const pageSizeOptions = options.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);

  const total = items.length;
  const rawCount = Math.ceil(total / pageSize);
  const pageCount = Math.max(1, rawCount);

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), pageCount));
  }, [pageCount]);

  const safePage = Math.min(Math.max(1, page), pageCount);
  const start = (safePage - 1) * pageSize;

  const pageSlice = useMemo(() => items.slice(start, start + pageSize), [items, start, pageSize]);

  const setPageSize = (n) => {
    const next = Number(n);
    if (!Number.isFinite(next) || next < 1) return;
    setPageSizeState(next);
    setPage(1);
  };

  const from = total === 0 ? 0 : start + 1;
  const to = Math.min(start + pageSize, total);

  return {
    page: safePage,
    setPage,
    pageSize,
    setPageSize,
    pageSizeOptions,
    pageCount,
    total,
    pageSlice,
    from,
    to
  };
}
